// Shared game page: https://app.puttr.co/game/:gameId (the app's Share game
// button). Mirrors puttr-app/components/profile/GameView.js - header, stats,
// session insights and the putt list - on the same black theme, for people
// who open the link without the app. With the app installed the link opens
// GameView in the app directly (universal / app links, see public/.well-known).
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBolt, faBullseye, faArrowUp, faArrowDown, faArrowTrendUp, faPlay } from '@fortawesome/free-solid-svg-icons';

import './GameView.css';
import { API_URL } from '../../constants/Strings';
import { primaryGreen, red } from '../../constants/Colors';
import { ReactComponent as Logo } from '../../images/logo_header.svg';
import { getHoleEntry, getTeeEntry, getEntryDescription } from './GameFunctions';
import { analyzeRollout, projectRollout, fmtRollout } from './RolloutFunctions';
import { getPuttAssessment } from './CoachFunctions';
import PuttCard from './PuttCard';
import Avatar from '../common/Avatar';
import PuttrSpinner from '../common/PuttrSpinner';
import OpenInApp, { APP_STORE_URL, PLAY_STORE_URL, isMobile } from '../common/OpenInApp';

export default function GameView() {

  const { gameId } = useParams();
  const [gameDetails, setGameDetails] = useState(null);
  const [gamePutts, setGamePutts] = useState(null);
  const [replay, setReplay] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    document.body.classList.add('dark');
    return () => document.body.classList.remove('dark');
  }, []);

  useEffect(() => {
    setGameDetails(null);
    setGamePutts(null);
    setFailed(false);

    axios.get(API_URL + '/club/analytics/game/' + gameId)
      .then(res => {
        if (!res.data || !res.data[0] || !res.data[0].game) setFailed(true);
        else setGameDetails(res.data);
      })
      .catch(() => setFailed(true));

    axios.get(API_URL + '/club/analytics/putts/' + gameId)
      .then(res => setGamePutts(Array.isArray(res.data) ? res.data : []))
      .catch(() => setGamePutts([]));

    // A finished reel (status 1) gets a Watch replay button; nothing on this
    // public page can start a render.
    axios.get(API_URL + '/video/replays/' + gameId)
      .then(res => {
        const r = Array.isArray(res.data) ? res.data[0] : null;
        if (r && r.status == 1 && r.videoUrl) setReplay(r);
      })
      .catch(() => {});
  }, [gameId]);

  const game = gameDetails ? gameDetails[0].game : null;
  const user = gameDetails && gameDetails[1] ? gameDetails[1].user : null;

  useEffect(() => {
    document.title = game ? `${game.title} · Puttr` : 'Puttr';
  }, [game]);

  // Same maths as the app's GameView analytics block.
  const analytics = useMemo(() => {
    if (!gamePutts || gamePutts.length === 0) return null;

    const assessment = getPuttAssessment(gamePutts, user ? user.handedness : null);

    let totalRollout = 0;
    let rolloutCount = 0;
    let centerCuts = 0;
    let madeCount = 0;

    for (const p of gamePutts) {
      let ballPath = p.ballPath;
      let holeBounds = p.holeBounds;
      if (typeof ballPath === 'string') ballPath = JSON.parse(ballPath);
      if (typeof holeBounds === 'string') holeBounds = JSON.parse(holeBounds);

      let ro = Number(p.rollout);
      if (!Number.isFinite(ro)) ro = projectRollout(analyzeRollout(ballPath, holeBounds), null, Number(p.made) === 0 ? false : undefined);
      if (ro != null && Number.isFinite(ro)) {
        totalRollout += ro;
        rolloutCount++;
      }

      if (p.made == 1) {
        madeCount++;
        const he = getHoleEntry(ballPath, holeBounds);
        const te = getTeeEntry(p.distance, p.tee);
        if (getEntryDescription(he[2] - te) === 'CENTER CUT') centerCuts++;
      }
    }

    return {
      ...assessment,
      avgRollout: rolloutCount > 0 ? totalRollout / rolloutCount : null,
      centerCutRate: madeCount > 0 ? Math.round((centerCuts / madeCount) * 100) : 0,
      totalMade: madeCount,
      totalPutts: gamePutts.length,
    };
  }, [gamePutts, user]);

  const appPath = 'game/' + gameId;

  const renderTopBar = () => (
    <div className="topBar">
      <a href="https://puttr.co" aria-label="Puttr"><Logo className="logo" /></a>
      <OpenInApp path={appPath} label={isMobile() ? "Open in app" : "Get the app"} />
    </div>
  );

  const renderHeader = () => (
    <div className="gcard hrow" style={{ marginTop: 8 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="gameTitle ellipsis">{game.title}</div>
        <div className="timeAgo">{moment(game.created_at).fromNow()}</div>
      </div>
      {user && (
        <div className="hrow" style={{ marginLeft: 12 }}>
          <div style={{ textAlign: 'right', marginRight: 10 }}>
            <div className="playerName ellipsis">{user.displayName}</div>
            {user.homeTown ? <div className="homeTown ellipsis">{user.homeTown}</div> : null}
          </div>
          <Avatar displayName={user.displayName} photoURL={user.photoURL} isMember={user.isMember} size={36} />
        </div>
      )}
    </div>
  );

  const renderStats = () => {
    const tot = gamePutts.length;
    const score = game.score;
    const perc = analytics ? analytics.percentMade : 0;
    return (
      <div className="statRow">
        <div className="statCard"><div className="statValue">{score > 0 ? score : '—'}</div><div className="statLabel">score</div></div>
        <div className="statCard"><div className="statValue">{tot}</div><div className="statLabel">putts</div></div>
        <div className="statCard"><div className="statValue">{perc}%</div><div className="statLabel">made</div></div>
      </div>
    );
  };

  const renderInsights = () => {
    if (!analytics) return null;
    const consistent = analytics.speedConsistency === 'very consistent' || analytics.speedConsistency === 'consistent';
    return (
      <div>
        <div className="sectionTitle">Session insights</div>
        <div className="gcard" style={{ marginTop: 10 }}>
          <div className="insightRow">
            <div className="insightIcon"><FontAwesomeIcon icon={faBolt} color={primaryGreen} /></div>
            <div style={{ flex: 1 }}>
              <div className="insightLabel">Avg pace</div>
              <div className="insightValue">{fmtRollout(analytics.avgRollout) || 'no read'}</div>
            </div>
            <div className="insightBadge" style={{ color: consistent ? primaryGreen : red }}>{analytics.speedConsistency}</div>
          </div>

          <div className="insightRow">
            <div className="insightIcon">
              {analytics.pushPull === 'push' ? <FontAwesomeIcon icon={faArrowUp} color={red} />
                : analytics.pushPull === 'pull' ? <FontAwesomeIcon icon={faArrowDown} color={red} />
                : <FontAwesomeIcon icon={faBullseye} color={primaryGreen} />}
            </div>
            <div style={{ flex: 1 }}>
              <div className="insightLabel">Tendency</div>
              <div className="insightValue">
                {analytics.pushPull === 'none' ? 'No push/pull tendency' : `${analytics.pushPull} (${analytics.pushPullPercent}%)`}
              </div>
            </div>
          </div>

          {(analytics.high > 0 || analytics.low > 0) && (
            <div className="insightRow">
              <div className="insightIcon"><FontAwesomeIcon icon={faArrowTrendUp} color="rgba(255,255,255,0.4)" /></div>
              <div style={{ flex: 1 }}>
                <div className="insightLabel">Breaking putts</div>
                <div className="insightValue">{analytics.high} high side, {analytics.low} low side</div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderActions = () => (
    <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {replay && (
        <a className="btnGreen" href={replay.videoUrl} target="_blank" rel="noreferrer">
          <FontAwesomeIcon icon={faPlay} /> Watch replay
        </a>
      )}
      <OpenInApp path={appPath} className="btnGhost" />
    </div>
  );

  const renderPutts = () => {
    if (game.gameCat === 'CHIPPING') {
      return (
        <div className="gcard center" style={{ marginTop: 16, padding: 24 }}>
          <div className="muted">Chipping session. Open it in the Puttr app for the heat map and chip-by-chip details.</div>
        </div>
      );
    }
    if (!gamePutts.length) {
      return (
        <div className="gcard center" style={{ marginTop: 16, padding: 24 }}>
          <div className="muted">No putts recorded</div>
        </div>
      );
    }
    return (
      <div>
        <div className="sectionTitle">All putts</div>
        <div className="puttList">
          {gamePutts.map((putt, index) => <PuttCard key={putt.id || index} putt={putt} index={index} />)}
        </div>
      </div>
    );
  };

  const renderFooter = () => (
    <div>
      <div className="footNote">Get Puttr and see every putt you hit</div>
      <div className="storeRow">
        <a className="btnGhost" href={APP_STORE_URL} target="_blank" rel="noreferrer">App Store</a>
        <a className="btnGhost" href={PLAY_STORE_URL} target="_blank" rel="noreferrer">Google Play</a>
      </div>
    </div>
  );

  if (failed) {
    return (
      <div className="darkPage">
        {renderTopBar()}
        <div className="page" style={{ paddingTop: 40 }}>
          <div className="gcard center">
            <div className="bold" style={{ fontSize: 18 }}>Game not found</div>
            <div className="muted" style={{ marginTop: 8, lineHeight: 1.5 }}>That link does not point at a game we can find. Ask for a fresh share link from the app.</div>
          </div>
          {renderFooter()}
        </div>
      </div>
    );
  }

  if (!game || !gamePutts) {
    return <PuttrSpinner dark title="LINING IT UP..." />;
  }

  return (
    <div className="darkPage">
      {renderTopBar()}
      <div className="page">
        {renderHeader()}
        {game.gameCat !== 'CHIPPING' && renderStats()}
        {game.gameCat !== 'CHIPPING' && renderInsights()}
        {renderActions()}
        {renderPutts()}
        {renderFooter()}
      </div>
    </div>
  );
}
